import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { SettingsService, _HttpClient } from '@delon/theme';

export interface HowToArticle {
  title: string;
  articleUrl: string;
}

/** Articles for one How-To scope (used when a route loads multiple scopes). */
export interface HowToArticlesByScope {
  scope: string;
  articles: HowToArticle[];
}

/**
 * Fetches contextual how-to articles for a screen from the backend.
 * Backend config is in Resources/app/qca/how-to-scopes-{country}.json.
 */
@Injectable({ providedIn: 'root' })
export class HowToService {
  constructor(
    private readonly http: _HttpClient,
    private readonly settings: SettingsService
  ) {}

  /**
   * Get country code for how-to (PH or MY). Defaults to PH.
   */
  getCountry(): string {
    const country = this.settings.user?.['company']?.country;
    if (country === 'MY' || country === 'PH') return country;
    return 'PH';
  }

  /**
   * Fetch articles for the given How-To scope and current company country.
   */
  getArticles(scope: string): Observable<HowToArticle[]> {
    if (!scope?.trim()) return of([]);
    const country = this.getCountry();
    return this.http
      .get('api/HowTo/articles', { scope: scope.trim(), country })
      .pipe(
        map((res: any) => {
          const list = res?.data ?? res;
          if (!Array.isArray(list)) return [];
          return list.map((row: any) => ({
            title: row.title ?? '',
            articleUrl: row.articleUrl ?? ''
          })) as HowToArticle[];
        }),
        catchError(() => of([]))
      );
  }

  /**
   * Fetch and merge articles for multiple scopes (order preserved; duplicates by URL + title removed).
   */
  getArticlesForScopes(scopes: string[]): Observable<HowToArticle[]> {
    const unique = [...new Set(scopes.map(s => s.trim()).filter(Boolean))];
    if (unique.length === 0) return of([]);
    if (unique.length === 1) return this.getArticles(unique[0]);
    return forkJoin(unique.map(s => this.getArticles(s))).pipe(
      map(lists => {
        const seen = new Set<string>();
        const merged: HowToArticle[] = [];
        for (const list of lists) {
          for (const a of list) {
            const key = `${a.articleUrl}\0${a.title}`;
            if (seen.has(key)) continue;
            seen.add(key);
            merged.push(a);
          }
        }
        return merged;
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Fetch articles per scope in route order; each scope keeps its own list (for grouped UI).
   */
  getArticlesGroupedByScope(
    scopes: string[]
  ): Observable<HowToArticlesByScope[]> {
    const ordered = scopes.map(s => s.trim()).filter(Boolean);
    const uniqueScopes = [...new Set(ordered)];
    if (uniqueScopes.length === 0) return of([]);

    return forkJoin(uniqueScopes.map(s => this.getArticles(s))).pipe(
      map(lists =>
        uniqueScopes.map((scope, i) => ({
          scope,
          articles: lists[i] ?? []
        }))
      ),
      catchError(() =>
        of(
          uniqueScopes.map(scope => ({
            scope,
            articles: [] as HowToArticle[]
          }))
        )
      )
    );
  }

  /**
   * Server-fetched HTML body for a QNE support KB article (allowlisted URLs only).
   */
  getArticleHtml(articleUrl: string): Observable<string | null> {
    if (!articleUrl?.trim()) return of(null);
    return this.http
      .get('api/HowTo/article-html', { url: articleUrl.trim() })
      .pipe(
        map((res: any) => {
          const payload = res?.data ?? res;
          const html = payload?.html;
          return typeof html === 'string' && html.length > 0 ? html : null;
        }),
        catchError(() => of(null))
      );
  }
}
